export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeBytes?: number;
}

export interface CompressionResult {
  file: File;
  base64string: string;
  ext: string;
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
}

class FileCompressionService {
  /**
   * Comprime una imagen o procesa un archivo (PDF/Imagen) retornando el File resultante y la cadena Base64 limpia.
   */
  public async compressFile(
    file: File,
    options: CompressionOptions = {}
  ): Promise<CompressionResult> {
    const {
      maxWidth = 1920,
      maxHeight = 1920,
      quality = 0.8,
      maxSizeBytes = 2 * 1024 * 1024, // 2MB por defecto para intentar comprimir por debajo de esto
    } = options;

    const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
    const isImage = ["png", "jpg", "jpeg", "webp"].includes(fileExt);

    // Si no es imagen (ej. PDF), devolvemos el archivo original codificado a Base64
    if (!isImage) {
      const base64string = await this.fileToBase64Clean(file);
      return {
        file,
        base64string,
        ext: fileExt,
        originalSize: file.size,
        compressedSize: file.size,
        wasCompressed: false,
      };
    }

    try {
      const compressedBlob = await this.compressImageToBlob(file, {
        maxWidth,
        maxHeight,
        quality,
        maxSizeBytes,
      });

      // Si el blob comprimido es mayor que el original, nos quedamos con el original
      if (compressedBlob.size >= file.size) {
        const base64string = await this.fileToBase64Clean(file);
        return {
          file,
          base64string,
          ext: fileExt === "jpeg" ? "jpg" : fileExt,
          originalSize: file.size,
          compressedSize: file.size,
          wasCompressed: false,
        };
      }

      const normalizedExt = "jpg"; // Salida estandarizada a JPG para compresión óptima
      const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      const base64string = await this.blobToBase64Clean(compressedBlob);

      return {
        file: compressedFile,
        base64string,
        ext: normalizedExt,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        wasCompressed: true,
      };
    } catch (error) {
      console.warn("Falló la compresión de imagen, utilizando archivo original:", error);
      const base64string = await this.fileToBase64Clean(file);
      return {
        file,
        base64string,
        ext: fileExt === "jpeg" ? "jpg" : fileExt,
        originalSize: file.size,
        compressedSize: file.size,
        wasCompressed: false,
      };
    }
  }

  private compressImageToBlob(
    file: File,
    options: { maxWidth: number; maxHeight: number; quality: number; maxSizeBytes: number }
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          // Calcular nuevas dimensiones manteniendo aspect ratio
          if (width > options.maxWidth || height > options.maxHeight) {
            if (width / height > options.maxWidth / options.maxHeight) {
              height = Math.round((height * options.maxWidth) / width);
              width = options.maxWidth;
            } else {
              width = Math.round((width * options.maxHeight) / height);
              height = options.maxHeight;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            return reject(new Error("No se pudo obtener contexto de canvas 2D"));
          }

          // Rellenar fondo blanco (por si la imagen PNG original tenía transparencias al pasar a JPEG)
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Primer intento de compresión
          let currentQuality = options.quality;
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error("Error al convertir canvas a Blob"));
              }

              // Si el tamaño aún supera maxSizeBytes y quality > 0.4, se hace un segundo intento con menos calidad
              if (blob.size > options.maxSizeBytes && currentQuality > 0.4) {
                canvas.toBlob(
                  (secondBlob) => {
                    resolve(secondBlob || blob);
                  },
                  "image/jpeg",
                  0.4
                );
              } else {
                resolve(blob);
              }
            },
            "image/jpeg",
            currentQuality
          );
        };
        img.onerror = (err) => reject(err);
        img.src = e.target?.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  private fileToBase64Clean(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const cleanBase64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(cleanBase64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private blobToBase64Clean(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const cleanBase64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(cleanBase64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export const fileCompressionService = new FileCompressionService();
